import type { TOC } from '@ember/component/template-only';
import { LinkTo } from '@ember/routing';
import { PhArrowRight } from 'ember-phosphor-icons';
import type { BetaAlert } from 'tio-employee/utils/beta/home-model';

/**
 * One row of the "Needs Your Attention" panel.
 *
 * Design: 48px tinted icon tile, title + tone badge, description, and a
 * right-aligned link with a trailing arrow. Tone comes from the smart action's
 * priority (urgent → danger/"Act Now", high → warning/"Due Soon").
 *
 * A row carrying an `action` opens something in place, so it renders a button and
 * needs `@onAction`; without one it stays inert rather than offering a dead control.
 */
const BetaHomeAlertRow: TOC<{
  Args: { alert: BetaAlert; onAction?: () => void };
  Element: HTMLDivElement;
}> = <template>
  <div class="tio-beta-home__alert" data-test-beta-alert={{@alert.id}} ...attributes>
    <div class="tio-beta-home__alert-body">
      <div class="tio-beta-home__alert-icon tio-beta-home__alert-icon--{{@alert.tone}}">
        {{#let @alert.icon as |Icon|}}
          <Icon @size={{24}} />
        {{/let}}
      </div>

      <div class="tio-col-extra-tight">
        <div class="tio-row-tight">
          <span class="tio-beta-home__alert-title">{{@alert.title}}</span>
          <span
            class="tio-beta-home__badge tio-beta-home__badge--{{@alert.tone}}"
          >{{@alert.badge}}</span>
        </div>
        <p class="tio-beta-home__alert-copy">{{@alert.description}}</p>
      </div>
    </div>

    {{#if @alert.action}}
      {{#if @onAction}}
        <button
          type="button"
          class="app-text-link app-text-link--icon"
          {{on "click" @onAction}}
          data-test-beta-alert-action={{@alert.id}}
        >
          <span>{{@alert.cta}}</span>
          <PhArrowRight @size={{16}} />
        </button>
      {{/if}}
    {{else if @alert.models}}
      <LinkTo
        @route={{if @alert.route @alert.route "authenticated.dashboard"}}
        @models={{@alert.models}}
        class="app-text-link app-text-link--icon"
      >
        <span>{{@alert.cta}}</span>
        <PhArrowRight @size={{16}} />
      </LinkTo>
    {{else}}
      <LinkTo
        @route={{if @alert.route @alert.route "authenticated.dashboard"}}
        class="app-text-link app-text-link--icon"
      >
        <span>{{@alert.cta}}</span>
        <PhArrowRight @size={{16}} />
      </LinkTo>
    {{/if}}
  </div>
</template>;

export default BetaHomeAlertRow;
